import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query/src/types';
import {
    getOnTopCosts,
    createOnTopCost,
    updateOnTopCost,
    updateOnTopCostsBulk,
    deleteOnTopCost,
    bulkCreateOnTopCosts,
    OnTopCostEdit,
    importOnTopCosts,
    exportOnTopCosts,
    downloadOnTopCostsImportTemplate,
    OnTopCostWithRelations,
    ImportOnTopCostsResponse,
} from '../../services/budgetOnTopCost';
import { QueryKey } from '../../enums/QueryKey';
import { queryClient } from '../../services/queryClient';
import { downloadBlobFile } from '../../utils/fileUtils';
import { useDispatchErrorNotification } from '../useDispatchErrorNotification';
import { useCreateRecord, useUpdateRecord, useDeleteRecord } from './crudOperations';

export function useOnTopCosts(versionId: number | undefined): [OnTopCostWithRelations[], boolean] {
    const {
        data: costs,
        isLoading,
        isFetching,
        isFetchedAfterMount,
    } = useQuery<OnTopCostWithRelations[], Error>({
        queryKey: [QueryKey.BudgetOnTopCosts, versionId],
        queryFn: () => getOnTopCosts(versionId!),
        initialData: [],
        enabled: !!versionId,
    });

    return [costs, isFetchedAfterMount ? isLoading : isFetching];
}

export function useCreateOnTopCost(
    versionId: number | undefined,
    { onSettled }: UseMutationOptions<OnTopCostWithRelations, Error, Record<string, unknown>>
) {
    const { t } = useTranslation('budget');
    return useCreateRecord<OnTopCostWithRelations, Error, Record<string, unknown>>({
        createRecordFn: (data) => createOnTopCost(versionId!, data),
        fetchRecordsQueryKey: [QueryKey.BudgetOnTopCosts, versionId],
        recordName: t('entity.onTopCost'),
        onSettled,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetOnTopCosts, versionId] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetSummary, versionId] });
        },
    });
}

export function useUpdateOnTopCost(
    versionId: number | undefined,
    { onSettled }: UseMutationOptions<OnTopCostWithRelations, Error, { id: number; [key: string]: unknown }> = {}
) {
    const { t } = useTranslation('budget');
    return useUpdateRecord<OnTopCostWithRelations, Error, { id: number; [key: string]: unknown }>({
        updateRecordFn: updateOnTopCost,
        fetchRecordsQueryKey: [QueryKey.BudgetOnTopCosts, versionId],
        recordName: t('entity.onTopCost'),
        onSettled: (data, error, variables, context) => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetSummary, versionId] });
            if (onSettled) onSettled(data, error, variables, context);
        },
    });
}

// CALL-779: the Overview's batch save for On Top Costs. Invalidates the summary as well, since a
// changed on-top amount moves the budget totals above the grid.
export function useUpdateOnTopCostsBulk(versionId: number | undefined) {
    const { t } = useTranslation('budget');
    return useUpdateRecord<OnTopCostWithRelations[], Error, OnTopCostEdit[]>({
        updateRecordFn: updateOnTopCostsBulk,
        fetchRecordsQueryKey: [QueryKey.BudgetOnTopCosts, versionId],
        recordName: t('onTopCosts.title'),
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetSummary, versionId] });
        },
    });
}

export function useDeleteOnTopCost(
    versionId: number | undefined,
    { onSettled }: UseMutationOptions<unknown, Error, number> = {}
) {
    const { t } = useTranslation('budget');
    return useDeleteRecord<unknown, Error, number>({
        deleteRecordFn: deleteOnTopCost,
        fetchRecordsQueryKey: [QueryKey.BudgetOnTopCosts, versionId],
        recordName: t('entity.onTopCost'),
        onSettled: (data, error, variables, context) => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetSummary, versionId] });
            if (onSettled) onSettled(data, error, variables, context);
        },
    });
}

export function useBulkCreateOnTopCosts(versionId: number | undefined) {
    const { t } = useTranslation('budget');
    return useCreateRecord<OnTopCostWithRelations[], Error, Record<string, unknown>[]>({
        createRecordFn: (costs) => bulkCreateOnTopCosts(versionId!, costs),
        fetchRecordsQueryKey: [QueryKey.BudgetOnTopCosts, versionId],
        recordName: t('onTopCosts.title'),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetOnTopCosts, versionId] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetSummary, versionId] });
        },
    });
}

export function useImportOnTopCosts(versionId: number | undefined) {
    return useMutation<ImportOnTopCostsResponse, Error, File>({
        mutationFn: (file) => importOnTopCosts(versionId!, file),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetOnTopCosts, versionId] });
            queryClient.invalidateQueries({ queryKey: [QueryKey.BudgetSummary, versionId] });
        },
    });
}

// CALL-757: export the version's on-top costs (server-built .xlsx in import format) and save it.
export function useExportOnTopCosts(versionId: number | undefined) {
    const { dispatchErrorNotification } = useDispatchErrorNotification();

    return useMutation<Blob, Error, void>({
        mutationFn: () => exportOnTopCosts(versionId!),
        onSuccess: (data) => {
            downloadBlobFile({
                data,
                fileName: `on-top-costs-v${versionId}.xlsx`,
                contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
    });
}

// CALL-750: download the sample import template (server-built .xlsx) and save it via the browser.
export function useDownloadOnTopCostsImportTemplate() {
    const { dispatchErrorNotification } = useDispatchErrorNotification();

    return useMutation<Blob, Error, void>({
        mutationFn: () => downloadOnTopCostsImportTemplate(),
        onSuccess: (data) => {
            downloadBlobFile({
                data,
                fileName: 'on-top-costs-import-template.xlsx',
                contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
        },
        onError: (error) => {
            dispatchErrorNotification({ message: error.message });
        },
    });
}
